'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useContext } from 'react';

import SegmentClick from '@flipster/features/src/misc/segment/segment-click';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { FormattedMessage } from '@flipster/i18n';
import { Margins, marginsBunja } from '@flipster/r230522/original/margin';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import Num, { countDecimalPlaces, numberFormatter } from '@flipster/utils/num';
import { StakeEvent } from '@flipster/utils/segment/events/stake';
import { OrderMarginType } from '@flipster/utils/types/base';

import { useSymbolMarginType } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/bunja/base';
import { maxPositionLimit } from '@web/constants/position';

import { PositionOrderButton, PositionOrderCard } from './position-order';
import { HedgingFormContext, hedgingFormBunja } from './state';

interface Props {
  onClose: () => void;
}

const HedgingForm = ({ onClose }: Props) => {
  const { stakingCurrency, stakedAmount } = useContext(HedgingFormContext);
  const { symbolInfoAtom, shortOpenPositionsAtom } = useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const shortOpenPositions = useAtomValue(shortOpenPositionsAtom);

  if (!symbolInfo) return null;

  const { unitOrderQty } = symbolInfo;

  return (
    <>
      <Modal.Content css={{ textStyle: 'Text/L/Regular', color: 'content.default.level-3' }}>
        <p>
          <FormattedMessage id='Autogen_common.r251126.s_Protect_your_staked_amount_from_price_drops_with_a_short_position' />
        </p>
        <dl className={css({ mt: 16 })}>
          <div className={sectionDescriptionItem}>
            <dt>
              <FormattedMessage id='Autogen_common.r251126.s_Total_staked' />
            </dt>
            <dd>
              <span className={sectionPrice}>
                {numberFormatter(stakedAmount, { precision: countDecimalPlaces(unitOrderQty), toFixed: true })}
              </span>
              <span>{stakingCurrency}</span>
            </dd>
          </div>
          <div className={sectionDescriptionItem}>
            <dt>
              <p>
                <FormattedMessage id='Autogen_common.r251126.s_Currently_hedged' />
              </p>
              <p>
                <FormattedMessage id='Autogen_common.r251126.s_All_short_positions' />
              </p>
            </dt>
            <dd>
              <span className={sectionPrice}>
                {numberFormatter(shortOpenPositions, { precision: countDecimalPlaces(unitOrderQty), toFixed: true })}
              </span>
              <span>{stakingCurrency}</span>
            </dd>
          </div>
        </dl>
        <PositionOrder />
      </Modal.Content>
      <Modal.Footer>
        <FooterButton onClose={onClose} />
      </Modal.Footer>
    </>
  );
};

export default HedgingForm;

const sectionDescriptionItem = flex({
  justifyContent: 'space-between',
  '& + &': {
    mt: 8,
  },
});

const sectionPrice = css({
  textStyle: 'Text/L/Strong',
  color: 'content.default.level-5',
  mr: 4,
});

const PositionOrder = () => {
  const { stakingCurrency } = useContext(HedgingFormContext);
  const { symbolInfoAtom, isHedgeOverStakingAmountAtom, isOverMaxPositionsAtom, marginAtom } =
    useBunja(hedgingFormBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const isHedgeOverStakingAmount = useAtomValue(isHedgeOverStakingAmountAtom);
  const isOverMaxPositions = useAtomValue(isOverMaxPositionsAtom);

  const { getMarginAtom } = useBunja(marginsBunja);
  const margins = useAtomValue(getMarginAtom);
  const marginType = useSymbolMarginType(symbolInfo?.symbol);
  const margin = useAtomValue(marginAtom);

  if (!symbolInfo) return null;

  const availableFunds = getAvailableFunds(marginType, margins, symbolInfo?.quoteCurrency);
  const isAvailableFunds = Num(availableFunds).isLessThan(margin);

  const contents = [
    {
      condition: isHedgeOverStakingAmount,
      description: <FormattedMessage id='Autogen_common.r251126.s_FullyHedged_Msg' />,
      isError: false,
    },
    {
      condition: isOverMaxPositions,
      description: (
        <FormattedMessage
          id='Autogen_common.r251126.s_PositionLimit_Msg'
          values={{
            word0: stakingCurrency,
            word1: maxPositionLimit,
            word2: maxPositionLimit,
          }}
        />
      ),
      isError: true,
    },
    {
      condition: isAvailableFunds,
      description: <FormattedMessage id='Autogen_common.r251126.s_InsufficientFunds_Msg' />,
      isError: true,
    },
  ].find(({ condition }) => condition);

  if (!contents) return <PositionOrderCard />;

  return (
    <div
      className={css({
        mt: 16,
        pt: 16,
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderColor: 'border.default.level-1',
      })}
    >
      <p
        className={css({
          textStyle: 'Text/L/Regular',
          color: contents.isError ? 'content.state.negative' : 'content.default.level-5',
        })}
      >
        {contents.description}
      </p>
    </div>
  );
};

interface FooterButtonProps {
  onClose: () => void;
}

const FooterButton = ({ onClose }: FooterButtonProps) => {
  const t = useTranslations();
  const openDepositModal = useDepositModal();
  const { stakingCurrency } = useContext(HedgingFormContext);
  const { isHedgeOverStakingAmountAtom, isOverMaxPositionsAtom, symbolInfoAtom, marginAtom } =
    useBunja(hedgingFormBunja);
  const { getMarginAtom } = useBunja(marginsBunja);

  const isHedgeOverStakingAmount = useAtomValue(isHedgeOverStakingAmountAtom);
  const isOverMaxPositions = useAtomValue(isOverMaxPositionsAtom);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const marginType = useSymbolMarginType(symbolInfo?.symbol);
  const margins = useAtomValue(getMarginAtom);
  const margin = useAtomValue(marginAtom);

  if (!symbolInfo) return null;

  const availableFunds = getAvailableFunds(marginType, margins, symbolInfo?.quoteCurrency);
  const isAvailableFunds = Num(availableFunds).isLessThan(margin);

  if (isHedgeOverStakingAmount)
    return (
      <SegmentClick
        event={StakeEvent.StakeHedgeSolutionClick}
        data={{ currency: stakingCurrency, button_text: 'Add more orders' }}
      >
        <Button
          as='a'
          href='/trade'
          onClick={onClose}
          hierarchy='primary'
          size='large'
          label={t('Autogen_common.r251126.s_Add_more_orders')}
        />
      </SegmentClick>
    );
  if (isOverMaxPositions)
    return (
      <SegmentClick
        event={StakeEvent.StakeHedgeSolutionClick}
        data={{ currency: stakingCurrency, button_text: 'Manage positions' }}
      >
        <Button
          as='a'
          href='/trade'
          onClick={onClose}
          hierarchy='primary'
          size='large'
          label={t('Autogen_common.r251126.s_Manage_positions')}
        />
      </SegmentClick>
    );
  if (isAvailableFunds)
    return (
      <SegmentClick
        event={StakeEvent.StakeHedgeSolutionClick}
        data={{ currency: stakingCurrency, button_text: 'Add funds' }}
      >
        <Button
          as='button'
          hierarchy='primary'
          onClick={() => {
            onClose();
            openDepositModal();
          }}
          size='large'
          label={t('Autogen_common.r251126.s_Add_funds')}
        />
      </SegmentClick>
    );
  return <PositionOrderButton onClose={onClose} />;
};

const getAvailableFunds = (marginType: OrderMarginType, margins: Margins, quoteCurrency: string) => {
  if (!margins) return '0';

  switch (marginType) {
    case 'Cross':
      return margins[quoteCurrency]?.crossMarginAvailable || '0';
    case 'Isolated':
      return margins[quoteCurrency]?.isolatedMarginAvailable || '0';
    default:
      marginType satisfies never;
      // oxlint-disable-next-line no-console
      console.error(`Unexpected marginType: ${marginType}`);
      return '0';
  }
};
